import {
  USER_LOAN_ESTIMATION_TYPE,
  USER_LOAN_PROGRAM,
} from '@/config/constants';
import type {
  LoanFlowProfile,
  LoanProductType,
} from '@/types/LoanApplicationType';

export type HubIntentResult =
  | {
      canCreate: true;
      productType: LoanProductType;
      flowProfile: LoanFlowProfile;
      investorModeAtCreation?: boolean;
      /** Passed to useInvestorAccess.checkAccess and PaywallModal */
      paywallLoanProgram: USER_LOAN_PROGRAM;
      /** Short phrase for CTA / body copy, e.g. "FHA purchase" */
      intentPhrase: string;
    }
  | {
      canCreate: false;
      userMessage: string;
    };

function agencyProductFromProgram(
  program: USER_LOAN_PROGRAM
): LoanProductType | null {
  switch (program) {
    case USER_LOAN_PROGRAM.FHA:
      return 'fha';
    case USER_LOAN_PROGRAM.VA:
      return 'va';
    case USER_LOAN_PROGRAM.USDA:
      return 'usda';
    case USER_LOAN_PROGRAM.CONVENTIONAL:
      return 'conventional';
    default:
      return null;
  }
}

/**
 * Maps profile loan estimation + program to POST /loan-applications body fields.
 * Refinance and commercial-only profiles do not get a hub "start" action here.
 */
export function resolveHubCreateIntent(
  loanEstimationType: USER_LOAN_ESTIMATION_TYPE | undefined,
  loanProgram: USER_LOAN_PROGRAM | undefined
): HubIntentResult {
  const est = loanEstimationType ?? USER_LOAN_ESTIMATION_TYPE.HOME_BUYER;
  const prog = loanProgram ?? USER_LOAN_PROGRAM.DEFAULT;

  if (est === USER_LOAN_ESTIMATION_TYPE.REFINANCE) {
    return {
      canCreate: false,
      userMessage:
        'Refinance applications are started from your refinance summary and property workspace, not from this page.',
    };
  }

  if (est === USER_LOAN_ESTIMATION_TYPE.COMMERCIAL) {
    return {
      canCreate: false,
      userMessage:
        'Choose a specific commercial or investor loan program under Loan type in your profile to start an application from here.',
    };
  }

  if (est === USER_LOAN_ESTIMATION_TYPE.HOME_BUYER) {
    if (prog === USER_LOAN_PROGRAM.CASH) {
      return {
        canCreate: false,
        userMessage:
          'Cash purchases do not use a mortgage application. Open a property in your deal room and continue from Qualify → Apply (cash readiness).',
      };
    }
    const pt =
      prog === USER_LOAN_PROGRAM.DEFAULT
        ? 'conventional'
        : agencyProductFromProgram(prog);
    if (!pt) {
      return {
        canCreate: false,
        userMessage:
          'Select a purchase loan program (Conventional, FHA, VA, USDA, or Cash) under Loan type in your profile.',
      };
    }
    const label =
      prog === USER_LOAN_PROGRAM.DEFAULT
        ? 'Conventional purchase'
        : `${String(prog).toUpperCase()} purchase`;
    return {
      canCreate: true,
      productType: pt,
      flowProfile: 'purchase',
      paywallLoanProgram:
        prog === USER_LOAN_PROGRAM.DEFAULT
          ? USER_LOAN_PROGRAM.CONVENTIONAL
          : prog,
      intentPhrase: label,
    };
  }

  // INVESTOR
  if (prog === USER_LOAN_PROGRAM.DSCR) {
    return {
      canCreate: true,
      productType: 'dscr',
      flowProfile: 'investor_dscr',
      investorModeAtCreation: true,
      paywallLoanProgram: USER_LOAN_PROGRAM.DSCR,
      intentPhrase: 'DSCR investor',
    };
  }
  if (prog === USER_LOAN_PROGRAM.BRIDGE) {
    return {
      canCreate: true,
      productType: 'bridge',
      flowProfile: 'purchase',
      paywallLoanProgram: USER_LOAN_PROGRAM.BRIDGE,
      intentPhrase: 'Bridge investor',
    };
  }
  if (prog === USER_LOAN_PROGRAM.CONVENTIONAL) {
    return {
      canCreate: true,
      productType: 'conventional',
      flowProfile: 'purchase',
      paywallLoanProgram: USER_LOAN_PROGRAM.CONVENTIONAL,
      intentPhrase: 'Conventional investor purchase',
    };
  }
  if (prog === USER_LOAN_PROGRAM.COMMERCIAL) {
    return {
      canCreate: true,
      productType: 'other',
      flowProfile: 'purchase',
      paywallLoanProgram: USER_LOAN_PROGRAM.COMMERCIAL,
      intentPhrase: 'Commercial investor',
    };
  }
  if (prog === USER_LOAN_PROGRAM.FIX_AND_FLIP) {
    return {
      canCreate: true,
      productType: 'other',
      flowProfile: 'purchase',
      paywallLoanProgram: USER_LOAN_PROGRAM.FIX_AND_FLIP,
      intentPhrase: 'Fix and flip',
    };
  }
  if (prog === USER_LOAN_PROGRAM.DEFAULT) {
    return {
      canCreate: true,
      productType: 'conventional',
      flowProfile: 'purchase',
      paywallLoanProgram: USER_LOAN_PROGRAM.CONVENTIONAL,
      intentPhrase: 'Conventional investor purchase',
    };
  }

  return {
    canCreate: false,
    userMessage:
      'This loan program is not started from the mortgage hub. Update Loan type in your profile or use Get Approved from a property.',
  };
}

export function hubIntentMatchesApplication(
  intent: Extract<HubIntentResult, { canCreate: true }>,
  flowProfile: LoanFlowProfile | undefined,
  productType: LoanProductType | undefined
): boolean {
  return (
    flowProfile === intent.flowProfile && productType === intent.productType
  );
}
